import React, { memo } from 'react';
import moment from 'moment';
import { LinearProgress } from '@mui/material';
import ExecutionStatusChip from './ExecutionStatusChip';
import ExecutionUnitsTable from './ExecutionUnitsTable';
import { CHIP_VARIANT_BY_RESULT, COMMAND_RESULT_BY_STATUS, EXECUTION_STATUS_CHIP, UNIT_NAME_BY_SCOPE, isExecutionFinished } from './executionLogs.config';
import { type IRemoteCommandExecution } from './RemoteCommandExecutionLogs';

interface ExecutionLogCardProps {
  title: string;
  commandLabel: string;
  targetLabel: string;
  execution: IRemoteCommandExecution | null;
}

const DATE_TIME_FORMAT = 'MMM DD, hh:mm A';
const TIME_FORMAT = 'hh:mm A';

function ExecutionLogCard({ title, commandLabel, targetLabel, execution }: ExecutionLogCardProps) {
  const isFinished = isExecutionFinished(execution);
  const statusChip = EXECUTION_STATUS_CHIP[execution?.executionStatus ?? 'NotPerformed'] ?? EXECUTION_STATUS_CHIP.NotPerformed;
  const percentage = execution?.executionPercentage ?? 0;

  const units = execution?.units ?? [];
  const unitName = execution?.scope ? UNIT_NAME_BY_SCOPE[execution?.scope] : undefined;
  const isUnitLevel = !!unitName && units?.length > 0;

  // Charger level executions show their outcome once finished, unit level ones show it per unit in the table
  const result = isFinished && !isUnitLevel && execution?.commandStatus ? COMMAND_RESULT_BY_STATUS[execution?.commandStatus] : null;

  return (
    <div className='remote__command__logs-card'>
      <div className='remote__command__logs-card-header'>
        <div className='remote__command__logs-card-title-wrap'>
          <span className='remote__command__logs-card-title'>{title}</span>
          <ExecutionStatusChip label={statusChip?.label} variant={statusChip?.variant} showDot />
          {(result === 'Success' || result === 'Failed') && <ExecutionStatusChip label={result} variant={CHIP_VARIANT_BY_RESULT[result]} showDot />}
          {execution?.requestedBy && <ExecutionStatusChip label={execution?.requestedBy} variant={'source ' + execution?.requestedBy?.toLowerCase()} />}
        </div>
        <div className='remote__command__logs-card-percentage'>
          {percentage}<span>%</span>
        </div>
      </div>

      <div className='remote__command__logs-card-sub-header'>
        <div className='remote__command__logs-card-description'>
          <span className='remote__command__logs-card-command'>{commandLabel}</span>
          {getDescription(execution, targetLabel, unitName, isUnitLevel)}
        </div>
        {isUnitLevel &&
          <span className='remote__command__logs-card-units'>
            {execution?.finishedUnits ?? 0} of {execution?.totalUnits ?? 0} units finished
          </span>
        }
      </div>
      <div className='remote__command__logs-card-initiated-by'>Initiated by {execution?.initiatedBy || 'NA'}</div>

      <LinearProgress
        className={'remote__command__logs-progress-bar' + (execution && !isFinished ? ' running' : '')}
        variant='determinate'
        value={percentage}
      />

      {!isUnitLevel && execution?.failureReason && <div className='remote__command__logs-card-failure'>{execution?.failureReason}</div>}
      {isUnitLevel && <ExecutionUnitsTable tableId={'remote-command-units-' + execution?.id} unitName={unitName} units={units} />}
    </div>
  );
}

// e.g. ' · Charger · Not started', ' · Charger · Started Oct 24, 10:48 AM · Downloading', ' · All SMRs · Oct 24, 10:41 AM – 10:42 AM'
function getDescription(execution: IRemoteCommandExecution | null, targetLabel: string, unitName: string | undefined, isUnitLevel: boolean): string {
  if (!execution) return ' · ' + targetLabel + ' · Not started';

  const units = execution?.units ?? [];
  const target = !unitName
    ? execution?.scope
    : units?.length === 1 ? unitName + ' ' + units[0]?.unitNumber : 'All ' + unitName + 's';

  const startedAt = moment(execution?.startedAt ?? execution?.createdAt);
  const completedAt = execution?.completedAt ? moment(execution?.completedAt) : null;
  const time = completedAt
    ? startedAt.format(DATE_TIME_FORMAT) + ' – ' + completedAt.format(completedAt.isSame(startedAt, 'day') ? TIME_FORMAT : DATE_TIME_FORMAT)
    : 'Started ' + startedAt.format(DATE_TIME_FORMAT);

  const showCommandStatus = !isUnitLevel && !isExecutionFinished(execution) && !!execution?.commandStatus && execution?.commandStatus !== 'Idle';

  return ' · ' + target + ' · ' + time + (showCommandStatus ? ' · ' + execution?.commandStatus : '');
}

export default memo(ExecutionLogCard);
